// src/services/image-upload-service.js
// Handles uploading site images to Firebase Storage instead of storing as base64

import { uploadFile, generateFileName } from './storage-service';
import { storage } from '../config/firebase-config';
import { ref, deleteObject } from 'firebase/storage';

/**
 * Uploads an image to Firebase Storage
 * @param {File} file - The image file to upload
 * @param {string} path - The storage path (e.g., 'hero-backgrounds', 'logos', 'gallery')
 * @param {string} userId - The practitioner's user ID
 * @returns {Promise<{success: boolean, url?: string, error?: string}>}
 */
export const uploadSiteImage = async (file, path, userId = 'default') => {
  // Validation checks
  if (!file) {
    return {
      success: false,
      error: 'No file provided'
    };
  }

  // Validate file type
  if (!file.type.startsWith('image/')) {
    return {
      success: false,
      error: 'File must be an image'
    };
  }

  // Validate file size (max 5MB)
  const MAX_SIZE = 5 * 1024 * 1024; // 5MB
  if (file.size > MAX_SIZE) {
    return {
      success: false,
      error: 'Image must be less than 5MB'
    };
  }

  // Try Firebase Storage first via storage-service
  try {
    const fileName = generateFileName(file.name);
    const storagePath = `${path}/${fileName}`;
    const { url, fullPath } = await uploadFile(storagePath, file);

    return {
      success: true,
      url,
      path: fullPath,   // kept for backward compatibility
      fullPath,         // explicit field for callers that need to delete later
      source: 'storage'
    };
  } catch (error) {
    // Firebase Storage not available — fall back to Base64
    console.warn('⚠️ Firebase Storage upload failed, using Base64 fallback:', error.message);

    try {
      const base64URL = await fileToBase64(file);
      return {
        success: true,
        url: base64URL,
        path: null,
        fullPath: null,
        source: 'base64'
      };
    } catch (base64Error) {
      console.error('Error converting to Base64:', base64Error);
      return {
        success: false,
        error: 'Failed to upload image'
      };
    }
  }
};

/**
 * Deletes an image from Firebase Storage
 * @param {string} storagePath - The full storage path from the upload response
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const deleteSiteImage = async (storagePath) => {
  try {
    if (!storage) {
      return {
        success: false,
        error: 'Firebase Storage is not initialized'
      };
    }

    if (!storagePath) {
      return { success: true }; // Nothing to delete
    }

    const storageRef = ref(storage, storagePath);
    await deleteObject(storageRef);

    return { success: true };
  } catch (error) {
    // If file doesn't exist, consider it a success
    if (error.code === 'storage/object-not-found') {
      return { success: true };
    }

    console.error('Error deleting image:', error);
    return {
      success: false,
      error: error.message || 'Failed to delete image'
    };
  }
};

/**
 * Converts a File or Blob to base64 (for preview purposes only, not storage)
 * @param {File|Blob} file - The file to convert
 * @returns {Promise<string>} - Base64 data URL
 */
export const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

/**
 * Compresses an image before upload (optional, for very large images)
 * @param {File} file - The image file
 * @param {number} maxWidth - Maximum width in pixels
 * @param {number} quality - JPEG quality 0-1
 * @returns {Promise<Blob>}
 */
export const compressImage = (file, maxWidth = 1920, quality = 0.8) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    img.onload = () => {
      // Calculate new dimensions
      let width = img.width;
      let height = img.height;

      if (width > maxWidth) {
        height = (height * maxWidth) / width;
        width = maxWidth;
      }

      canvas.width = width;
      canvas.height = height;

      // Draw and compress
      ctx.drawImage(img, 0, 0, width, height);

      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('Failed to compress image'));
          }
        },
        'image/jpeg',
        quality
      );
    };

    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
};

export default {
  uploadSiteImage,
  deleteSiteImage,
  fileToBase64,
  compressImage
};
